import { Column, SortingState } from '@tanstack/react-table';
import { isEmpty, isFunction } from 'lodash';
import { ArrowDownWideNarrow, ArrowUpWideNarrow, ChartNoAxesGantt, Check, List, Settings2 } from 'lucide-react';

import { Button } from '@/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DATA_TABLE_LAYOUTS, SHIPMENT_GROUPING } from '@/lib/constants';
import { ShipmentGrouping } from '@/lib/models/shipment/types';
import { OptionItem, TableLayout } from '@/types';
import { cn } from '@/utils/styles';

export interface DataTableViewOptionsProps<TData = any, TValue = any> {
  columns: Column<TData, TValue>[];
  sorting?: SortingState;
  layout?: TableLayout;
  grouping?: ShipmentGrouping;
  onLayoutChange?: (layout: TableLayout) => void;
  onSortingChange?: (sorting: SortingState) => void;
  onGroupingChange?: (grouping: ShipmentGrouping) => void;
  onEditColumns?: () => void;
}

const DataTableViewOptions = ({
  columns,
  sorting,
  layout,
  grouping,
  onLayoutChange,
  onSortingChange,
  onGroupingChange,
  onEditColumns,
}: DataTableViewOptionsProps) => {
  const hasSortValue = sorting && !isEmpty(sorting);
  const sortByValue = hasSortValue ? sorting[0].id : '';
  const sortByDesc = hasSortValue ? sorting[0].desc : true;

  const sortableColumns = columns.reduce((accum, col) => {
    if (col.getCanSort()) {
      const { header, id } = col.columnDef;
      return [
        ...accum,
        {
          value: id || '',
          label: header?.toString() || '',
        },
      ];
    }
    return accum;
  }, [] as OptionItem[]);

  const handleSortChange = (val: string) => {
    if (isFunction(onSortingChange)) {
      const direction = sortByValue === val ? !sortByDesc : sortByDesc;
      onSortingChange([
        {
          id: val,
          desc: direction,
        },
      ]);
    }
  };

  const handleChangeLayout = (value: TableLayout) => {
    if (isFunction(onLayoutChange)) onLayoutChange(value);
  };

  const handleGroupingChange = (value: string) => {
    const newGrouping = (value || null) as ShipmentGrouping;
    if (isFunction(onGroupingChange)) {
      onGroupingChange(newGrouping);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="small" variant="outline" className="ml-auto flex h-8" iconLeft={<Settings2 className="size-4" />}>
          View
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36 bg-white">
        <DropdownMenuLabel>Layout</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            className={cn('cursor-pointer', {
              'bg-lightBlue-50': layout === DATA_TABLE_LAYOUTS.TABLE,
            })}
            onClick={() => handleChangeLayout(DATA_TABLE_LAYOUTS.TABLE)}
          >
            <List className="mr-2 size-4" />
            List
          </DropdownMenuItem>
          <DropdownMenuItem
            className={cn('cursor-pointer', {
              'bg-lightBlue-50': layout === DATA_TABLE_LAYOUTS.TIMELINE,
            })}
            onClick={() => handleChangeLayout(DATA_TABLE_LAYOUTS.TIMELINE)}
          >
            <ChartNoAxesGantt className="mr-2 size-4" />
            Timeline
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuLabel className="mt-3">Sort by</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={sortByValue} onValueChange={handleSortChange}>
          {sortableColumns.map((col) => {
            return (
              <DropdownMenuRadioItem
                key={col.value}
                value={col.value}
                indicatorIcon={sortByDesc ? <ArrowDownWideNarrow /> : <ArrowUpWideNarrow />}
              >
                {col.label}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
        <DropdownMenuLabel>Group by</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={grouping} onValueChange={handleGroupingChange}>
          {Object.values(SHIPMENT_GROUPING).map((value, i) => {
            return (
              <DropdownMenuRadioItem
                key={i}
                value={value}
                className={cn('cursor-pointer', {
                  'bg-lightBlue-50': grouping === value,
                })}
                indicatorIcon={grouping === value ? <Check /> : null}
              >
                {value}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem className="pt-2 hover:!bg-white">
            <Button size="small" className="w-full" onClick={onEditColumns}>
              Edit columns
            </Button>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { DataTableViewOptions };
